import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Clock, FileText, FolderOpen, X, Plug } from 'lucide-react';
import { AppLayout } from '../components/layout/AppLayout';
import {
  getProjects,
  createProject,
  getCurrentUser,
  Project,
} from '../services/firebase';
import { formatDistanceToNow } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { StatCard, Badge } from '../components';

export function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Buildlog — Projects';
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const user = getCurrentUser();
      if (!user) return;
      const list = await getProjects(user.uid);
      setProjects(list);
    } catch (err) {
      setError('Could not load your projects. Try refreshing.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const activeProjects = projects.filter(p => p.status === 'active').length;

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center py-40">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-[3px] border-slate-900 border-t-transparent" />
      </div>
    </AppLayout>
  );

  if (error) return (
    <AppLayout>
      <div className="text-center py-40 text-red-500 font-medium font-mono text-sm">{error}</div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="space-y-8 pb-20">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-slate-200 pb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-display tracking-tight">
              Your projects
            </h1>
            <p className="text-slate-500 mt-1 text-sm">Each project has its own voice, structure, and build log.</p>
          </div>
          <button
            onClick={() => setShowNewProject(true)}
            className="bg-slate-900 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-sm active:scale-95"
          >
            <Plus size={16} />
            New project
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Total projects" value={projects.length} icon={<FolderOpen className="text-slate-900" />} />
          <StatCard label="Active" value={activeProjects} icon={<Clock className="text-accent" />} />
          <StatCard label="Archived" value={projects.filter(p => p.status === 'completed').length} icon={<FileText className="text-slate-400" />} />
        </div>

        {/* Projects Grid */}
        <section>
          {projects.length > 0 && (
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-4 font-bold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              Active projects
            </h2>
          )}

          {projects.length === 0 ? (
            <div className="space-y-4 mt-8 max-w-lg mx-auto">

              {/* Primary empty state */}
              <div className="relative flex flex-col items-center justify-center py-24 px-4 text-center bg-slate-50 border border-slate-200 rounded-3xl overflow-hidden animate-in fade-in duration-500">
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-multiply"
                  style={{
                    backgroundImage: `url('/fingerprint-pattern.svg')`,
                    backgroundSize: '150%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
                <div className="relative z-10 flex flex-col items-center">
                  <div className="w-16 h-16 bg-white border border-slate-200 rounded-2xl flex items-center justify-center mb-6 shadow-sm relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-100 to-transparent opacity-50" />
                    <div className="text-accent">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12V15C22 16.6569 20.6569 18 19 18C17.3431 18 16 16.6569 16 15V12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12V15C8 18.866 11.134 22 15 22H16"/><path d="M12 12V15"/><path d="M8 12C8 9.79086 9.79086 8 12 8"/><path d="M12 12C14.2091 12 16 10.2091 16 8"/></svg>
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900 font-display tracking-tight mb-3">
                    Start your first project
                  </h2>

                  <p className="text-slate-500 text-[15px] leading-relaxed mb-8 px-6">
                    A project is where you set your voice, choose your structure, and drop in your build notes. Buildlog turns what you log into posts worth publishing.
                  </p>

                  <button
                    onClick={() => setShowNewProject(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white font-semibold text-sm rounded-xl hover:bg-slate-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 active:scale-95"
                  >
                    <Plus size={16} />
                    Create your first project
                  </button>

                  <div className="mt-10 flex items-center gap-2 text-[10px] font-mono text-slate-400 uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                    Ready when you are
                  </div>
                </div>
              </div>

              {/* Secondary: Connect Claude prompt */}
              <button
                onClick={() => navigate('/settings')}
                className="w-full flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-2xl hover:border-slate-900 transition-colors text-left group"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0 group-hover:border-slate-400 transition-colors">
                  <Plug size={16} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">Connect Claude</p>
                  <p className="text-xs text-slate-400 mt-0.5">Let Claude log entries and read your projects directly from your conversations.</p>
                </div>
                <span className="text-slate-300 group-hover:text-slate-600 transition-colors text-lg leading-none">→</span>
              </button>

            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map(project => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-slate-900 hover:shadow-md transition-all duration-200 flex flex-col h-full relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-slate-100 group-hover:bg-accent transition-colors" />

                  <div className="flex items-start justify-between mb-4 pl-2">
                    <h3 className="font-bold text-lg text-slate-900 font-display tracking-tight">
                      {project.name}
                    </h3>
                    <Badge status={project.status} />
                  </div>

                  <p className="text-slate-500 text-sm mb-6 flex-grow line-clamp-2 pl-2 leading-relaxed">
                    {project.description || 'No description added yet.'}
                  </p>

                  <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono pt-4 border-t border-slate-100 pl-2">
                    {project.tags && project.tags.length > 0 ? (
                      <span className="flex gap-1.5">
                        {project.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded text-slate-500">#{tag}</span>
                        ))}
                        {project.tags.length > 2 && <span>+{project.tags.length - 2}</span>}
                      </span>
                    ) : (
                      <span>No tags</span>
                    )}
                    <span className="ml-auto flex items-center gap-1">
                      <Clock size={10} />
                      {formatDistanceToNow(project.updatedAt.toDate())} ago
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {showNewProject && (
        <NewProjectModal
          onClose={() => setShowNewProject(false)}
          onSaved={() => { setShowNewProject(false); loadProjects(); }}
        />
      )}
    </AppLayout>
  );
}

function NewProjectModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void; }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [status, setStatus] = useState<'active' | 'archived' | 'completed'>('active');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const user = getCurrentUser();
      if (!user) return;
      await createProject({
        userId: user.uid,
        name: name.trim(),
        description: description.trim(),
        systemPrompt: systemPrompt.trim(),
        status,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      onSaved();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">

        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="text-accent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12V15C22 16.6569 20.6569 18 19 18C17.3431 18 16 16.6569 16 15V12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12V15C8 18.866 11.134 22 15 22H16"/><path d="M12 12V15"/><path d="M8 12C8 9.79086 9.79086 8 12 8"/><path d="M12 12C14.2091 12 16 10.2091 16 8"/></svg>
            </div>
            <h2 className="text-[15px] font-bold text-slate-900 font-display">
              New project
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-1.5">
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold">Project name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-slate-900 focus:bg-white transition-colors"
              placeholder="e.g., Agentic Commerce API"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold">What are you building?</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full h-20 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-slate-900 focus:bg-white transition-colors resize-none"
              placeholder="A short description of the product or feature this project tracks."
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold flex items-center gap-2">
              Extra instructions for Claude <span className="text-[9px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">OPTIONAL</span>
            </label>
            <textarea
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              className="w-full h-20 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono focus:outline-none focus:border-slate-900 focus:bg-white transition-colors resize-none"
              placeholder="e.g., Prioritise information density over flow. Always include a specific metric."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value as typeof status)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-slate-900 focus:bg-white transition-colors"
              >
                <option value="active">Active</option>
                <option value="completed">Archived</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 font-bold">Tags</label>
              <input
                type="text"
                value={tags}
                onChange={e => setTags(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-slate-900 focus:bg-white transition-colors"
                placeholder="infrastructure, api"
              />
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-slate-100 flex justify-end gap-3 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="px-6 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl hover:bg-slate-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {saving ? 'Creating...' : 'Create project'}
          </button>
        </div>
      </div>
    </div>
  );
}